import React, { useEffect, useMemo, useRef, useState } from "react";
import { useRecommendations } from "./providers/RecommendationsProvider.js";

import { Recommendation } from "./components/Recommendation.js";
import { useTrackVisibility } from "./hooks/trackVisibility.js";
import { i18n } from "@lingui/core";

const isVisible = (element: HTMLElement | null, threshold: number) => {
	if (!element) return false;
	const rect = element.getBoundingClientRect();
	const elementHeight = rect.height;
	if (elementHeight === 0) return false;

	const visibleTop = Math.max(rect.top, 0);
	const visibleBottom = Math.min(rect.bottom, window.innerHeight || document.documentElement.clientHeight);
	const visibleHeight = visibleBottom - visibleTop;

	if (visibleHeight <= 0) return false;

	const percentVisible = visibleHeight / elementHeight;

	return percentVisible >= threshold;
};

export const InfiniteScroll = () => {
	const { items, fetchMore, fetchingMore, fetchingInitial, type } = useRecommendations();
	const [observerTriggered, setObserverTriggered] = useState(false);
	const filteredRecommendations = useMemo(() => items.filter(({ price }) => !!price) || [], [items]);

	useTrackVisibility(filteredRecommendations);

	useEffect(() => {
		const check = async () => {
			const bottomVisible = isVisible(bottomRef.current, 0.4);
			if (bottomVisible && !fetchingMore) {
				await fetchMore();
			}
			if (observerTriggered) {
				setObserverTriggered(false);
			}
		};
		check();
	}, [observerTriggered, filteredRecommendations]);

	const bottomRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setObserverTriggered(true);
				}
			},
			{
				threshold: 0.4,
				root: null,
			},
		);

		if (bottomRef.current) {
			observer.observe(bottomRef.current);
		}
		return () => {
			observer.disconnect();
		};
	}, []);

	const title =
		type === "inventory"
			? i18n.t({
					id: `mobility-recommendations.moreAdsFromDealer`,
					message: "More ads from us",
					comment: "Text for 'more ads like this' podlet when we only show ads from the same dealer",
				})
			: i18n.t({
					id: `mobility-recommendations.moreLikeThis`,
					message: "More like this",
					comment: "Text for 'more ads like this' podlet",
				});

	if (filteredRecommendations.length === 0 && !fetchingInitial) {
		return null;
	}

	return (
		<div className="my-40">
			<h2 className="text-ml my-16">{title}</h2>
			<div
				className="relative flex justify-center flex-wrap gap-y-16 gap-x-8 md:gap-16 md:mr-0 pl-2"
				style={{ scrollbarWidth: "none" }}
			>
				{filteredRecommendations.map((recommendation) => {
					return <Recommendation key={recommendation.itemId} recommendation={recommendation} />;
				})}
				<div ref={bottomRef} className="absolute w-full min-h-[40vh] max-h-[40%] bottom-0 pointer-events-none"></div>
			</div>
		</div>
	);
};
